import { useState, useEffect, useRef } from "react"
import { useParams } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog"
import { useToast } from "@/hooks/use-toast"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  ShieldCheck, PenTool, CheckCircle2,
  Phone, Check, ShieldAlert, Award
} from "lucide-react"
import { decodePayload, encodePayload } from "@/utils/shareEncoder"
import type { Contrato, StatusContrato } from "@/types/comercial"
import { supabase } from "@/integrations/supabase/client"

interface Signatario {
  nome: string
  email: string
  role: string
  status: "assinado" | "pendente"
  dataAssinatura?: string
  ipAddress?: string | null
  assinaturaBase64?: string
}

interface AuditEvent {
  timestamp: string
  action: string
  details: string
}

interface ContratoExtendido extends Contrato {
  envelopeId?: string
  signatarios?: Signatario[]
  auditLog?: AuditEvent[]
}

type AcessoPublico = "ok" | "senha_requerida" | "senha_incorreta"

/*
 * O anon não lê nem altera a tabela contratos: a página usa as RPCs
 * get_public_contrato e assinar_contrato (20261015100000_public_links_rpcs.sql),
 * que conferem a senha no servidor, não devolvem senha nem observações
 * internas e registram o IP real da requisição.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function contratoFromRpc(c: any): ContratoExtendido {
  return {
    id: c.id,
    titulo: c.titulo,
    clienteNome: c.cliente_nome,
    clienteEmail: c.cliente_email || "",
    clienteEmpresa: c.cliente_empresa || undefined,
    status: c.status as StatusContrato,
    conteudo: c.conteudo || "",
    valor: Number(c.valor || 0),
    dataInicio: c.data_inicio,
    dataFim: c.data_fim || undefined,
    dataAssinatura: c.data_assinatura || undefined,
    createdAt: c.created_at,
    updatedAt: c.updated_at,
    envelopeId: `#${String(c.id).slice(0, 8).toUpperCase()}`,
    signatarios: c.signatarios || [],
    auditLog: c.audit_log || [],
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function parseRpc(data: any): { acesso: AcessoPublico; contrato?: ContratoExtendido } | null {
  if (!data) return null
  return { acesso: data.acesso, contrato: data.contrato ? contratoFromRpc(data.contrato) : undefined }
}

function fmtCurrency(v: number) {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v)
}

function fmtDate(d: string) {
  if (!d) return "—"
  return new Date(d + "T00:00:00").toLocaleDateString("pt-BR")
}

const ContratoPublico = () => {
  const { token } = useParams<{ token: string }>()
  const { toast } = useToast()
  const [contrato, setContrato] = useState<ContratoExtendido | null>(null)
  const [loading, setLoading] = useState(true)
  const [erro, setErro] = useState(false)
  const [senhaDigitada, setSenhaDigitada] = useState("")
  const [senhaErro, setSenhaErro] = useState("")
  const [senhaValida, setSenhaValida] = useState(false)
  // Contrato do banco protegido: só chega depois que o servidor confere a senha
  const [senhaRequerida, setSenhaRequerida] = useState(false)
  const [senhaAcesso, setSenhaAcesso] = useState<string | undefined>()
  const [validandoSenha, setValidandoSenha] = useState(false)

  useEffect(() => {
    if (contrato) {
      if (!contrato.senha) {
        setSenhaValida(true)
      }
    }
  }, [contrato])

  const handleValidarSenha = async (e: React.FormEvent) => {
    e.preventDefault()
    // Link legado com o contrato embutido na URL: a senha veio junto no payload
    if (contrato) {
      if (senhaDigitada === contrato.senha) {
        setSenhaValida(true)
        setSenhaAcesso(senhaDigitada)
        setSenhaErro("")
      } else {
        setSenhaErro("Senha incorreta. Tente novamente.")
      }
      return
    }
    if (!token || !senhaDigitada) return
    setValidandoSenha(true)
    try {
      const { data, error } = await supabase.rpc("get_public_contrato", { p_id: token, p_senha: senhaDigitada })
      if (error) throw error
      const res = parseRpc(data)
      if (res?.acesso === "ok" && res.contrato) {
        setSenhaAcesso(senhaDigitada)
        setSenhaRequerida(false)
        setSenhaValida(true)
        setSenhaErro("")
        setContrato(res.contrato)
      } else {
        setSenhaErro("Senha incorreta. Tente novamente.")
      }
    } catch (err) {
      console.error("Erro ao validar senha do contrato:", err)
      setSenhaErro("Não foi possível validar a senha agora. Tente novamente.")
    } finally {
      setValidandoSenha(false)
    }
  }

  // Dialog de Assinatura
  const [signingOpen, setSigningOpen] = useState(false)
  const [selectedSignerIdx, setSelectedSignerIdx] = useState<number | null>(null)
  const [assinando, setAssinando] = useState(false)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const [isDrawing, setIsDrawing] = useState(false)
  const [hasDrawing, setHasDrawing] = useState(false)

  useEffect(() => {
    if (!token) { setErro(true); setLoading(false); return }

    // Link legado: o contrato inteiro vem codificado na URL
    if (token.startsWith("_data_")) {
      const decoded = decodePayload<ContratoExtendido>(token)
      if (decoded) setContrato(decoded)
      else setErro(true)
      setLoading(false)
      return
    }

    const fetchContrato = async () => {
      try {
        const { data, error } = await supabase.rpc("get_public_contrato", { p_id: token })
        if (error) throw error
        const res = parseRpc(data)
        if (!res) setErro(true)
        else if (res.acesso === "ok" && res.contrato) setContrato(res.contrato)
        else setSenhaRequerida(true)
      } catch (err) {
        console.error("Erro ao buscar contrato:", err)
        setErro(true)
      } finally {
        setLoading(false)
      }
    }

    fetchContrato()
  }, [token])

  // ── Canvas Signpad Logic ────────────────────────────────────────────────────
  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext("2d")
    if (!ctx) return

    ctx.lineWidth = 2.5
    ctx.lineCap = "round"
    ctx.strokeStyle = "#1e3a8a" // azul escuro

    const rect = canvas.getBoundingClientRect()
    let clientX = 0
    let clientY = 0

    if ("touches" in e) {
      clientX = e.touches[0].clientX
      clientY = e.touches[0].clientY
      e.preventDefault() // prevent scroll on touch
    } else {
      clientX = e.clientX
      clientY = e.clientY
    }

    ctx.beginPath()
    ctx.moveTo(clientX - rect.left, clientY - rect.top)
    setIsDrawing(true)
    setHasDrawing(true)
  }

  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext("2d")
    if (!ctx) return

    const rect = canvas.getBoundingClientRect()
    let clientX = 0
    let clientY = 0

    if ("touches" in e) {
      clientX = e.touches[0].clientX
      clientY = e.touches[0].clientY
      e.preventDefault()
    } else {
      clientX = e.clientX
      clientY = e.clientY
    }

    ctx.lineTo(clientX - rect.left, clientY - rect.top)
    ctx.stroke()
  }

  const stopDrawing = () => {
    setIsDrawing(false)
  }

  const clearCanvas = () => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext("2d")
    if (!ctx) return
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    setHasDrawing(false)
  }

  const handleOpenSignature = (idx: number) => {
    setSelectedSignerIdx(idx)
    setHasDrawing(false)
    setSigningOpen(true)
  }

  const handleSaveSignature = async () => {
    const canvas = canvasRef.current
    if (!canvas || !contrato || selectedSignerIdx === null) return
    if (!hasDrawing) {
      toast({ title: "Assinatura em branco", description: "Desenhe sua assinatura antes de confirmar.", variant: "destructive" })
      return
    }

    setAssinando(true)
    try {
      // O servidor grava a assinatura, o IP da requisição e o evento de auditoria
      const { data, error } = await supabase.rpc("assinar_contrato", {
        p_id: contrato.id,
        p_senha: senhaAcesso ?? null,
        p_signatario: selectedSignerIdx,
        p_assinatura: canvas.toDataURL("image/png"),
      })
      if (error) throw error
      const res = parseRpc(data)
      if (res?.contrato) setContrato(res.contrato)
      setSigningOpen(false)
      toast({
        title: "Contrato Assinado!",
        description: `Sua assinatura foi registrada em ${new Date().toLocaleTimeString("pt-BR")}.`
      })
    } catch (err) {
      console.error("Erro ao registrar assinatura:", err)
      const message = err instanceof Error ? err.message : (err as { message?: string })?.message
      toast({
        title: "Assinatura não registrada",
        description: message || "Verifique sua conexão e tente novamente.",
        variant: "destructive"
      })
    } finally {
      setAssinando(false)
    }
  }

  const getWhatsappLink = () => {
    if (!contrato) return "#"
    const isBase64Token = token && token.startsWith("_data_")
    const shareToken = isBase64Token ? encodePayload(contrato) : contrato.id
    const newPublicUrl = `${window.location.origin}/c/${shareToken}`

    const todosAssinaram = contrato.signatarios?.every(s => s.status === "assinado")
    const text = todosAssinaram
      ? `Olá! O contrato *${contrato.titulo}* foi assinado por todas as partes. Veja a cópia assinada no link:\n${newPublicUrl}`
      : `Olá! Assinei a minha parte no contrato *${contrato.titulo}*.\n\nVocê pode assinar ou visualizar a auditoria neste link:\n${newPublicUrl}`

    return `https://wa.me/5521998231720?text=${encodeURIComponent(text)}`
  }

  const senhaModal = (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/85 backdrop-blur-sm p-4 animate-fadeIn">
      <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 max-w-md w-full text-center shadow-2xl relative overflow-hidden">
        <div className="absolute -right-16 -top-16 w-32 h-32 bg-blue-500/10 rounded-full blur-2xl" />
        <ShieldCheck className="h-14 w-14 text-blue-500 mx-auto mb-4 animate-pulse" />

        <h1 className="text-2xl font-black text-white mb-2">Acesso Restrito</h1>
        <p className="text-slate-400 text-sm leading-relaxed mb-6">
          Este contrato contém informações confidenciais (LGPD) e está protegido por senha. Por favor, insira a senha para visualizar.
        </p>

        <form onSubmit={handleValidarSenha} className="space-y-4">
          <div className="space-y-1.5 text-left">
            <Label htmlFor="senha_acesso" className="text-slate-350 text-xs font-bold uppercase tracking-wider pl-1 font-semibold text-slate-300">Senha de Acesso</Label>
            <Input
              id="senha_acesso"
              type="password"
              value={senhaDigitada}
              onChange={e => setSenhaDigitada(e.target.value)}
              placeholder="Digite a senha..."
              className="bg-slate-950 border-slate-800 text-white rounded-xl py-3 focus:border-blue-500 focus:ring-blue-500 placeholder-slate-600 text-center w-full"
              autoFocus
            />
          </div>

          {senhaErro && (
            <p className="text-red-400 text-xs font-semibold pl-1 text-center">
              {senhaErro}
            </p>
          )}

          <Button
            type="submit"
            disabled={validandoSenha}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3.5 rounded-xl transition-all shadow-lg hover:shadow-blue-900/40"
          >
            {validandoSenha ? "Verificando..." : "Visualizar Contrato"}
          </Button>
        </form>
      </div>
    </div>
  )

  // ── Loading ──
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4 text-center">
          <div className="relative w-16 h-16">
            <div className="absolute inset-0 rounded-full border-4 border-blue-500/20"></div>
            <div className="absolute inset-0 rounded-full border-4 border-t-blue-500 border-r-blue-500 animate-spin"></div>
          </div>
          <p className="text-blue-400 font-medium animate-pulse text-sm">Carregando Contrato Seguro...</p>
        </div>
      </div>
    )
  }

  // ── Senha exigida pelo servidor (nenhum dado do contrato foi carregado) ──
  if (senhaRequerida && !contrato) {
    return <div className="min-h-screen bg-slate-950">{senhaModal}</div>
  }

  // ── Erro ──
  if (erro || !contrato) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <div className="bg-slate-900 border border-slate-800 rounded-3xl p-8 max-w-md w-full text-center shadow-2xl">
          <ShieldAlert className="h-14 w-14 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-black text-white mb-2">Contrato não encontrado</h1>
          <p className="text-slate-400 text-sm leading-relaxed mb-6">
            O link de assinatura eletrônica é inválido, expirou ou o payload está corrompido. Contate o remetente do contrato.
          </p>
          <a href="https://wa.me/5521998231720" target="_blank" rel="noopener noreferrer"
            className="w-full inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white font-bold py-3 rounded-xl transition-all">
            <Phone className="h-4 w-4" />
            Suporte ItaCondo
          </a>
        </div>
      </div>
    )
  }

  const isAtivo = contrato.status === "ativo"

  const showBlur = !senhaValida && contrato?.senha

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 pb-28 font-sans relative">
      <div className={`transition-all duration-500 ${showBlur ? 'filter blur-md pointer-events-none select-none' : ''}`}>
      
      {/* Header */}
      <header className="no-print border-b border-white/10 bg-[#0A0F1E] px-4 py-4">
        <div className="mx-auto flex max-w-4xl items-center justify-between gap-4">
          <img src="/logo_sfundo.png" alt="itaCondo Sistemas" className="-my-2 -ml-3 h-12 w-auto object-contain sm:h-14" />
          <div className="flex items-center gap-3">
            <span className="hidden items-center gap-1.5 text-[11px] text-slate-400 sm:flex">
              <ShieldCheck className="h-3.5 w-3.5 text-emerald-400" />Assinatura eletrônica segura
            </span>
            <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold ${isAtivo ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-400/15 text-amber-300"}`}>
              <span className={`h-1.5 w-1.5 rounded-full ${isAtivo ? "bg-emerald-400" : "bg-amber-300"}`} />
              {isAtivo ? "Assinado" : "Aguardando assinatura"}
            </span>
          </div>
        </div>
      </header>

      {/* Main Container */}
      <div className="max-w-4xl mx-auto px-4 mt-8 space-y-8">

        {/* Document Body Sheet (Paper look) */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-[0_10px_40px_rgba(15,23,42,0.08)] print-card relative overflow-hidden">
          {/* Letterhead: dark band so the white "ita" of the logo stays legible */}
          <div className="flex flex-col gap-4 bg-[#0A0F1E] px-8 py-6 sm:flex-row sm:items-center sm:justify-between sm:px-12 [print-color-adjust:exact] [-webkit-print-color-adjust:exact]">
            <img src="/logo_sfundo.png" alt="itaCondo Sistemas" className="-my-4 -ml-5 h-20 w-auto object-contain object-left sm:h-24" />
            <div className="text-left text-[11px] leading-relaxed text-slate-400 sm:text-right">
              <div className="font-semibold uppercase tracking-[0.12em] text-slate-300">Contrato de prestação de serviços</div>
              {contrato.envelopeId && <div className="font-mono">{contrato.envelopeId}</div>}
              <div>Emitido em {new Date(contrato.createdAt).toLocaleDateString("pt-BR")}</div>
            </div>
          </div>
          <div className="h-1 bg-gradient-to-r from-blue-600 via-sky-400 to-blue-600 [print-color-adjust:exact]" />

          <div className="relative p-8 sm:p-12">
          {/* Legal Stamp watermark on background if signed */}
          {isAtivo && (
            <div className="absolute right-8 top-8 pointer-events-none no-print rotate-6 rounded-lg border-2 border-emerald-600/40 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.15em] text-emerald-600/60">
              Assinado digitalmente
            </div>
          )}

          <div className="mb-8 space-y-5">
            <div>
              <h2 className="text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">Instrumento particular de prestação de serviços</h2>
              <p className="mt-1 text-sm text-slate-500">{contrato.titulo}</p>
            </div>
            <dl className="grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-slate-200 bg-slate-200 text-sm sm:grid-cols-4">
              {[
                ["Contratante", contrato.clienteEmpresa || contrato.clienteNome],
                ["Responsável", contrato.clienteNome],
                ["Valor", fmtCurrency(contrato.valor)],
                ["Vigência", `${fmtDate(contrato.dataInicio)}${contrato.dataFim ? " a " + fmtDate(contrato.dataFim) : ""}`],
              ].map(([label, value]) => (
                <div key={label} className="bg-white px-4 py-3">
                  <dt className="text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-400">{label}</dt>
                  <dd className="mt-1 truncate font-semibold text-slate-800">{value}</dd>
                </div>
              ))}
            </dl>
          </div>

          {/* Contract Content Rendered */}
          <div className="text-slate-700 text-sm leading-relaxed whitespace-pre-line space-y-4 font-serif">
            {contrato.conteudo}
          </div>

          <div className="border-t my-10 pt-6"></div>

          {/* Render Drawn Signatures inside the paper doc */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 mt-6">
            {(contrato.signatarios || []).map((s, idx) => (
              <div key={idx} className="flex flex-col items-center text-center p-4 border border-dashed rounded-xl bg-slate-50/50">
                <p className="text-xs text-slate-400 font-bold uppercase tracking-wider mb-2">{s.role}</p>
                
                {s.status === "assinado" && s.assinaturaBase64 ? (
                  <div className="h-24 flex items-center justify-center bg-white border rounded-lg p-2 w-full max-w-[240px] shadow-inner relative">
                    <img src={s.assinaturaBase64} alt={`Assinatura de ${s.nome}`} className="max-h-full max-w-full object-contain" />
                    <CheckCircle2 className="h-5 w-5 text-emerald-500 absolute bottom-1.5 right-1.5 bg-white rounded-full" />
                  </div>
                ) : (
                  <div className="h-24 flex flex-col items-center justify-center w-full max-w-[240px] border border-dashed border-slate-200 rounded-lg text-slate-400 no-print">
                    <PenTool className="h-6 w-6 animate-pulse text-amber-500 mb-1" />
                    <span className="text-[10px] font-semibold uppercase tracking-wider">Aguardando Assinatura</span>
                    
                    {/* Exibe botão se for esta parte pendente */}
                    <Button size="sm" onClick={() => handleOpenSignature(idx)} className="mt-2 h-7 bg-blue-600 hover:bg-blue-700 text-white font-bold text-[10px] px-3.5 rounded-lg shadow-sm">
                      Assinar Agora
                    </Button>
                  </div>
                )}
                
                <p className="font-bold text-slate-800 text-xs mt-3">{s.nome}</p>
                <p className="text-slate-400 text-[10px]">{s.email}</p>
                {s.status === "assinado" && s.dataAssinatura && (
                  <div className="mt-1 text-[9px] text-slate-500 space-y-0.5 leading-none">
                    <p>IP: {s.ipAddress || "não registrado"}</p>
                    <p>Data: {new Date(s.dataAssinatura).toLocaleString("pt-BR")}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
          </div>
        </div>

        {/* Audit Log / Verification details */}
        <div className="bg-slate-900 text-slate-300 rounded-2xl p-6 shadow-md border border-slate-800 no-print space-y-4">
          <h3 className="font-bold text-sm text-white flex items-center gap-2 border-b border-slate-800 pb-2">
            <ShieldCheck className="h-5 w-5 text-emerald-400" />
            Histórico e Registro de Auditoria do Envelope
          </h3>
          
          <div className="space-y-3 font-mono text-[10px] max-h-44 overflow-y-auto pr-2 divide-y divide-slate-800/40">
            {(contrato.auditLog || []).map((e, idx) => (
              <div key={idx} className="pt-2 flex justify-between gap-6 leading-normal">
                <span className="text-slate-500 shrink-0">{new Date(e.timestamp).toLocaleString("pt-BR")}</span>
                <span className="font-bold text-emerald-400 shrink-0 uppercase tracking-wider">{e.action}</span>
                <span className="text-slate-400 text-right flex-1">{e.details}</span>
              </div>
            ))}
          </div>

          <div className="border-t border-slate-800 pt-3.5 flex flex-col sm:flex-row justify-between items-center gap-3">
            <div className="text-[10px] text-slate-400 flex items-center gap-1.5">
              <Award className="h-4 w-4 text-blue-400" />
              <span>Assinatura eletrônica em conformidade com o Artigo 10, § 2º da MP nº 2.200-2/2001.</span>
            </div>
            <Button size="sm" onClick={() => window.print()} className="bg-slate-800 hover:bg-slate-700 text-white text-xs border border-slate-700">
              Imprimir Via PDF
            </Button>
          </div>
        </div>

        {/* Sync WhatsApp button for client */}
        <div className="bg-white border rounded-2xl p-5 shadow-sm no-print flex flex-col sm:flex-row items-center justify-between gap-4">
          <div>
            <h4 className="font-bold text-slate-900 text-sm">Sincronizar Assinaturas com o Comercial</h4>
            <p className="text-xs text-slate-400 mt-0.5">Clique para notificar a ItaCondo e sincronizar este contrato com o sistema do administrador.</p>
          </div>
          <a href={getWhatsappLink()} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white text-xs font-bold px-5 py-3 rounded-xl transition-all shadow shadow-green-900/10">
            <Phone className="h-4 w-4 text-white" />
            Notificar Conclusão no WhatsApp
          </a>
        </div>

      </div>

      {/* ── Signature Drawing Pad Dialog ── */}
      <Dialog open={signingOpen} onOpenChange={setSigningOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Assinar Documento Digitalmente</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-xs text-slate-500 leading-normal">
              <p>Utilize o mouse (computador) ou o dedo/caneta (telas touch) para desenhar sua assinatura oficial no painel abaixo.</p>
              <p className="mt-1 font-semibold text-slate-600">O endereço IP da sua conexão será registrado pelo servidor junto com a assinatura.</p>
            </div>
            
            <div className="border border-slate-200 rounded-xl overflow-hidden bg-slate-50 relative">
              <canvas
                ref={canvasRef}
                width={400}
                height={160}
                className="w-full h-[160px] bg-white cursor-crosshair touch-none"
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
              />
              <div className="absolute left-4 bottom-2 text-[9px] text-slate-400 pointer-events-none select-none">
                Área de desenho da assinatura digital
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2 pt-4 border-t flex flex-row justify-between w-full">
            <Button variant="ghost" onClick={clearCanvas} className="text-slate-500 hover:text-slate-700 text-xs px-3">
              Limpar Desenho
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setSigningOpen(false)}>Cancelar</Button>
              <Button onClick={handleSaveSignature} disabled={assinando} className="bg-blue-600 hover:bg-blue-700 text-white gap-1 text-xs px-4">
                <Check className="h-4 w-4" />
                Confirmar Assinatura
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      </div>

      {/* Modal de Senha */}
      {showBlur && senhaModal}
    </div>
  )
}

export default ContratoPublico
